import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IService, IServiceCategory } from '../../models/IService';
import ServiceButton from '../../views/scheduling-widget/widget-wizard/forms/service-step/ServiceButton';
import { getNewMultiServicesValue } from '../../utils/services';
import { Box, useMediaQuery } from '@mui/material';
import ServiceInfoDialog from '../../views/scheduling-widget/widget-wizard/forms/service-step/ServiceInfoDialog';
import ServiceInfo from '../../views/scheduling-widget/widget-wizard/forms/service-step/ServiceInfo';
import WidgetButton from '../../views/scheduling-widget/components/WidgetButton';
import StyledBackButton from '../../views/scheduling-widget/components/StyledBackButton';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import WidgetServiceOption from './WidgetServiceOption';

interface IWidgetServiceSelectProps {
    options: IService[];
    value: IService[];
    onChange: (v: IService[]) => void;
    useMultiservices?: boolean;
    disableFn?: (s: IService) => boolean;
    openInfoCb?: () => void;
    closeInfoCb?: () => void;
    onServiceOptionToggle?: (id: number | string) => void;
    selectedServiceOptions?: Array<number | string>;
}

const WidgetServiceSelect: FC<IWidgetServiceSelectProps> = ({
    options,
    value,
    onChange,
    useMultiservices = false,
    disableFn,
    openInfoCb,
    closeInfoCb,
    onServiceOptionToggle,
    selectedServiceOptions
}) => {
    const matchDownMd = useMediaQuery('(max-width: 800px)');
    const [selectedCategory, setSelectedCategory] = useState<number | 'all' | null>(null);
    const [selectedService, setSelectedService] = useState<IService | null>(null);

    const usedCategories = useMemo(
        () =>
            options.reduce<IServiceCategory[]>((acc, current) => {
                const newCategories = current.categories?.filter(({ id }) => !acc.some((cat) => cat.id === id)) ?? [];
                if (newCategories.length) {
                    const combined = [...acc, ...newCategories];
                    const nullOrder = combined.length + 2;

                    return combined.sort((prev, next) => (prev.sorting_order ?? nullOrder) - (next.sorting_order ?? nullOrder));
                }

                return acc;
            }, []),
        [options]
    );

    const filteredServicesByCategory = useMemo(() => {
        if (selectedCategory === 'all') {
            return options;
        }
        return options.filter(({ categories }) => categories?.some((category) => category.id === selectedCategory));
    }, [options, selectedCategory]);

    const isSelected = useCallback((serviceId: number) => value.some(({ id }) => id === serviceId), [value]);

    const handleSelectService = useCallback(
        (arg: IService) => {
            if (isSelected(arg.id)) {
                // Deselect service options
                const selectedOptions = arg.options?.filter((opt) => selectedServiceOptions?.includes(opt.id)) ?? [];
                selectedOptions.forEach(({ id }) => {
                    onServiceOptionToggle?.(id);
                });

                // Deselect service
                const newValue = useMultiservices ? [...value].filter((s) => s.id !== arg.id) : [];
                onChange(newValue);
            } else {
                const ids = [...value, arg].map(({ id }) => id);
                const newIds = getNewMultiServicesValue(ids, options);
                const newValue = useMultiservices
                    ? options?.filter(({ id }) => newIds.includes(id)).sort((a, b) => newIds.indexOf(a.id) - newIds.indexOf(b.id))
                    : [arg];
                onChange(newValue);
            }
        },

        [isSelected, onChange, onServiceOptionToggle, options, selectedServiceOptions, useMultiservices, value]
    );

    const handleDialogOpen = useCallback(
        (service: IService) => {
            setSelectedService(service);
            openInfoCb?.();
        },
        [openInfoCb]
    );

    const handleDialogClose = useCallback(() => {
        setSelectedService(null);
        closeInfoCb?.();
    }, [closeInfoCb]);

    const onChoose = useCallback(() => {
        if (selectedService) {
            handleSelectService(selectedService);
            handleDialogClose();
        }
    }, [handleDialogClose, handleSelectService, selectedService]);

    useEffect(() => {
        if (!selectedCategory && !usedCategories.length) {
            setSelectedCategory('all');
        }
    }, [selectedCategory, usedCategories.length]);

    if (selectedService && matchDownMd) {
        return <ServiceInfo service={selectedService} onClose={handleDialogClose} onChoose={onChoose} />;
    }

    return (
        <>
            {selectedCategory ? (
                <>
                    {!!usedCategories.length && (
                        <Box mb={2}>
                            <StyledBackButton variant="contained" startIcon={<ArrowBackIos />} onClick={() => setSelectedCategory(null)}>
                                Back to Categories List
                            </StyledBackButton>
                        </Box>
                    )}

                    {filteredServicesByCategory.map((service: IService) => (
                        <Box key={service.id}>
                            <ServiceButton
                                service={service}
                                onChoose={() => {
                                    handleSelectService(service);
                                    handleDialogClose();
                                }}
                                isSelected={isSelected(service.id)}
                                handleClickInfo={() => {
                                    handleDialogOpen(service);
                                }}
                                disabled={disableFn ? disableFn(service) : undefined}
                            />
                            {isSelected(service.id) && service.options?.length ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 1 }}>
                                    {service.options.map((option) => (
                                        <WidgetServiceOption
                                            key={option.id}
                                            option={option}
                                            isSelected={selectedServiceOptions?.includes(option.id)}
                                            onClick={() => onServiceOptionToggle?.(option.id)}
                                        />
                                    ))}
                                </Box>
                            ) : null}
                        </Box>
                    ))}
                </>
            ) : (
                <>
                    {usedCategories.map((category) => (
                        <WidgetButton
                            onChoose={() => setSelectedCategory(category.id)}
                            name={category.name}
                            isSelected={false}
                            hideAvatar
                            customClassName={category.custom_css ?? undefined}
                        />
                    ))}
                    <WidgetButton onChoose={() => setSelectedCategory('all')} name="Show All Services" isSelected={false} hideAvatar />
                </>
            )}

            {!!selectedService && !matchDownMd && (
                <ServiceInfoDialog open onChoose={onChoose} onClose={handleDialogClose} service={selectedService} />
            )}
        </>
    );
};

export default WidgetServiceSelect;
